import type { BreadcrumbSegment } from '@/components/ui/address-bar'
import type { BasecampTodoList } from '@/types/basecamp'

export type BasecampBreadcrumbItemType =
  | 'projects'
  | 'project'
  | 'todoset'
  | 'todolist'
  | 'section'


export interface BasecampBreadcrumbItem {
  id: string
  title: string
  type: BasecampBreadcrumbItemType
}

/** Resolve project id from integrationData or basecamp-project-* tab id. */
export function resolveBasecampTabProjectId(tab: {
  id?: string
  integrationData?: { projectId?: string }
}): string | null {
  const fromData = tab.integrationData?.projectId
  if (fromData) return String(fromData)
  const match = tab.id?.match(/^basecamp-project-(.+)$/)
  return match?.[1] ?? null
}

/** True when the document-viewer toolbar should render BasecampIntegrationBreadcrumbs. */
export function shouldShowBasecampChromeBreadcrumbs(tab: {
  id?: string
  type?: string
  integrationData?: { projectId?: string; section?: string }
} | null | undefined): boolean {
  if (!tab?.type) return false
  if (
    tab.type === 'integration-basecamp-todo' ||
    tab.type === 'integration-basecamp-todo-list'
  ) {
    return true
  }
  if (tab.type !== 'integration-basecamp') return false
  if (tab.integrationData?.section) return true
  if (resolveBasecampTabProjectId(tab)) return true
  return false
}

export function buildBasecampProjectHomeTrail(
  projectId: string,
  projectName: string,
): BasecampBreadcrumbItem[] {
  return [
    { id: 'projects', title: 'Projects', type: 'projects' },
    { id: projectId, title: projectName, type: 'project' },
  ]
}

export interface BuildBasecampBreadcrumbTrailOptions {
  projectId: string
  projectName: string
  listId: string
  todoLists: BasecampTodoList[]
}

/**
 * Build ordered Basecamp breadcrumb trail for a todo list (or nested section).
 * Matches Basecamp UX: Project > To-Dos > [parent lists...] > current list.
 */
export function buildBasecampBreadcrumbTrail({
  projectId,
  projectName,
  listId,
  todoLists,
}: BuildBasecampBreadcrumbTrailOptions): BasecampBreadcrumbItem[] {
  const currentList = todoLists.find((list) => list.id?.toString() === listId?.toString())
  if (!currentList) {
    return [
      { id: 'projects', title: 'Projects', type: 'projects' },
      { id: projectId, title: projectName, type: 'project' },
      { id: 'todoset', title: 'To-Dos', type: 'todoset' },
    ]
  }

  const items: BasecampBreadcrumbItem[] = [
    { id: 'projects', title: 'Projects', type: 'projects' },
    { id: projectId, title: projectName, type: 'project' },
    { id: 'todoset', title: 'To-Dos', type: 'todoset' },
  ]

  const ancestors: BasecampBreadcrumbItem[] = []
  let parentId = currentList.parent_id?.toString() ?? null
  const seen = new Set<string>()

  while (parentId && !seen.has(parentId)) {
    seen.add(parentId)
    const parent = todoLists.find((list) => list.id?.toString() === parentId)
    if (!parent) {
      ancestors.unshift({
        id: parentId,
        title: currentList.parent_title || 'Todo List',
        type: 'todolist',
      })
      break
    }
    ancestors.unshift({
      id: parent.id,
      title: parent.title,
      type: parent.is_nested ? 'section' : 'todolist',
    })
    parentId = parent.parent_id?.toString() ?? null
  }

  items.push(...ancestors)
  items.push({
    id: currentList.id,
    title: currentList.title,
    type: currentList.is_nested ? 'section' : 'todolist',
  })

  return items
}

export interface BasecampBreadcrumbNavigateContext {
  projectId: string
  projectName: string
  organizationId?: string
  workspaceId?: string | null
}

function dispatchIntegrationViewChange(detail: Record<string, unknown>) {
  if (isSameBasecampDestination(window.location.pathname, detail)) {
    pulseBasecampCurrentColumn()
    return
  }
  window.dispatchEvent(new CustomEvent('integration-view-change', { detail }))
}

export function isSameBasecampDestination(
  pathname: string,
  dest: {
    tabId?: unknown
    type?: unknown
    integrationData?: {
      projectId?: string
      listId?: string
      todoId?: string
      section?: string
    }
  },
): boolean {
  const tabId = typeof dest.tabId === 'string' ? dest.tabId : ''
  const type = typeof dest.type === 'string' ? dest.type : ''
  const data = dest.integrationData ?? {}
  if (tabId === 'basecamp-projects' || data.section === 'projects') {
    return /\/basecamp\/projects\/?$/.test(pathname)
  }
  if (type === 'integration-basecamp-todo-list' || tabId.startsWith('basecamp-todo-list-')) {
    const listId = data.listId ?? tabId.replace(/^basecamp-todo-list-/, '')
    return Boolean(listId) && pathname.includes(`/todo-list/${listId}`)
  }
  if (type === 'integration-basecamp-todo' || /^basecamp-todo-\d/.test(tabId)) {
    const todoId = data.todoId ?? tabId.replace(/^basecamp-todo-/, '')
    return Boolean(todoId) && pathname.includes(`/todo/${todoId}`)
  }
  if (type === 'integration-basecamp' && data.projectId) {
    return (
      pathname.includes(`/project/${data.projectId}`) &&
      !pathname.includes('/todo/') &&
      !pathname.includes('/todo-list/')
    )
  }
  return false
}

export function pulseBasecampCurrentColumn(): void {
  const el =
    document.querySelector('[data-testid="basecamp-todo-list-viewer"]') ||
    document.querySelector('[data-testid="basecamp-todos-panel"]') ||
    document.querySelector('[data-testid="document-viewer-content"]')
  if (!(el instanceof HTMLElement)) return
  el.classList.remove('rail-suggested-pulse')
  void el.offsetWidth
  el.classList.add('rail-suggested-pulse')
  window.setTimeout(() => el.classList.remove('rail-suggested-pulse'), 2200)
}

export function basecampBreadcrumbItemsToAddressBarSegments(
  items: BasecampBreadcrumbItem[],
  ctx: BasecampBreadcrumbNavigateContext,
  options?: { includeCurrent?: boolean }
): BreadcrumbSegment[] {
  const includeCurrent = options?.includeCurrent ?? true
  const visibleItems = includeCurrent ? items : items.slice(0, -1)

  return visibleItems.map((item, index) => {
    const isLast = index === visibleItems.length - 1
    const orgPayload =
      ctx.organizationId || ctx.workspaceId
        ? { organizationId: ctx.organizationId ?? ctx.workspaceId }
        : {}

    if (item.type === 'projects') {
      // Section hub crumb (e.g. Projects list) is current when alone / last
      if (isLast && includeCurrent && item.id !== 'projects') {
        return { label: item.title, path: null }
      }
      if (isLast && includeCurrent && items.length === 1) {
        return { label: item.title, path: null }
      }
      return {
        label: item.title,
        path: 'basecamp-projects',
        onClick: () => {
          dispatchIntegrationViewChange({
            tabId: 'basecamp-projects',
            title: 'Projects',
            type: 'integration-basecamp',
            workspaceId: ctx.workspaceId,
            integrationData: {
              integrationId: 'basecamp',
              section: 'projects',
              ...orgPayload,
            },
          })
        },
      }
    }

    if (item.type === 'project') {
      if (isLast && includeCurrent) {
        return { label: item.title, path: null }
      }
      return {
        label: item.title,
        path: `project-${item.id}`,
        onClick: () => {
          dispatchIntegrationViewChange({
            tabId: `basecamp-project-${item.id}`,
            title: item.title,
            type: 'integration-basecamp',
            workspaceId: ctx.workspaceId,
            integrationData: {
              integrationId: 'basecamp',
              projectId: item.id,
              projectName: item.title,
              ...orgPayload,
            },
          })
        },
      }
    }

    if (item.type === 'todoset') {
      return {
        label: item.title,
        path: null,
      }
    }

    if (item.type === 'todolist' || item.type === 'section') {
      if (isLast && includeCurrent) {
        return {
          label: item.title,
          path: null,
        }
      }
      return {
        label: item.title,
        path: `list-${item.id}`,
        onClick: () => {
          dispatchIntegrationViewChange({
            tabId: `basecamp-todo-list-${item.id}`,
            title: item.title,
            type: 'integration-basecamp-todo-list',
            workspaceId: ctx.workspaceId,
            integrationData: {
              integrationId: 'basecamp',
              projectId: ctx.projectId,
              listId: item.id,
              projectName: ctx.projectName,
              listName: item.title,
              ...orgPayload,
            },
          })
        },
      }
    }

    return { label: item.title, path: null }
  })
}

/** Serializable trail stored on integrationData for toolbar rendering. */
export function serializeBreadcrumbTrail(items: BasecampBreadcrumbItem[]): BasecampBreadcrumbItem[] {
  return items.map(({ id, title, type }) => ({ id, title, type }))
}

export function dispatchIntegrationTabEnrich(detail: {
  tabId: string
  breadcrumbTrail: BasecampBreadcrumbItem[]
  title?: string
  listName?: string
  projectName?: string
}) {
  window.dispatchEvent(
    new CustomEvent('integration-tab-enrich', {
      detail: {
        ...detail,
        breadcrumbTrail: serializeBreadcrumbTrail(detail.breadcrumbTrail),
      },
    })
  )
}
